import z from "zod";
import { ethnicityCodes, majorCodes, majorCodesWithName, nationalityCodes, requestTypeList } from "./shared";
import { masterDegreesApi } from "@/lib";
import { preCheckExistWrapper } from "@/lib/utils/func";

export type tMasterDegreeSchema = z.infer<typeof masterDegreeSchema>;

export const masterDegreeSchema = z
	.object({
		id: z.string().optional(),
		majorCode: z
			.string()
			.nonempty("Bắt buộc nhập")
			.refine((val) => !val || majorCodes.includes(val), { message: "Mã ngành không hợp lệ" }),
		major: z.string().nonempty("Bắt buộc nhập"),
		cccd: z.string()
			.nonempty("Bắt buộc nhập")
			.refine((v) => !v || /^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9}|[a-zA-Z0-9]{12})$/.test(v), "CCCD sai định dạng"),
		currentMajorCode: z.string().optional(),
		currentCCCD: z.string().optional(),
		studentId: z.string().nonempty("Bắt buộc nhập").max(250, { message: "Tối đa 250 ký tự" }),
		currentStudentId: z.string().optional(),
		studentName: z.string().nonempty("Bắt buộc nhập").max(250, { message: "Tối đa 250 ký tự" }),
		dateOfBirth: z.date({ required_error: "Bắt buộc nhập" }).optional().refine((val) => {
			if (!val) return true;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const cutoff = new Date(today);
			cutoff.setDate(today.getDate() - 1);
			const dob = new Date(val);
			dob.setHours(0, 0, 0, 0);
			return dob <= cutoff;
		}, {
			message: "Ngày sinh không hợp lệ",
		}),
		placeOfBirth: z.string().max(250, { message: "Tối đa 250 ký tự" }).optional(),
		gender: z.string().nonempty("Bắt buộc chọn"),
		ethnicCode: z
			.string()
			.optional()
			.refine((val) => !val || ethnicityCodes.includes(val), { message: "Mã dân tộc không hợp lệ" }),
		ethnicity: z.string().optional(),
		nationalityCode: z
			.string()
			.refine((val) => !val || nationalityCodes.includes(val), { message: "Mã quốc tịch không hợp lệ" })
			.optional(),
		nationality: z.string().optional(),
		graduationYear: z.coerce
			.string()
			.nonempty("Bắt buộc nhập")
			.max(4, "Năm không hợp lệ")
			.refine((val) => parseInt(val) >= 1995 && parseInt(val) <= new Date().getFullYear(), { message: "Năm tốt nghiệp không hợp lệ" }),
		trainingMode: z.string().nonempty("Bắt buộc chọn"),
		signedBy: z.string().nonempty("Bắt buộc nhập"),
		signerTitle: z.string().nonempty("Bắt buộc nhập"),
		signDate: z.date({ required_error: "Bắt buộc nhập" }).optional().refine((val) => {
			if (!val) return true;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const cutoff = new Date(today);
			cutoff.setDate(today.getDate() - 1);
			const dob = new Date(val);
			dob.setHours(0, 0, 0, 0);
			return dob <= cutoff;
		}, {
			message: "Ngày ký không hợp lệ",
		}),
		course: z.string().nonempty("Bắt buộc nhập"),
		defenseDate: z.date({ required_error: "Bắt buộc nhập" }).optional().refine((val) => {
			if (!val) return false;
			return val <= new Date()
		}, {
			message: "Ngày bảo vệ không được vượt quá ngày hiện tại",
		}),
		currentDegreeNumber: z.string().optional(),
		degreeNumber: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"), //= Số hiệu bằng,
		registrationNumber: z.string().max(50, { message: "Tối đa 50 ký tự" }).nonempty("Bắt buộc nhập"), //= Số vào sổ
		recognitionDecisionNumber: z.string().max(250, { message: "Tối đa 250 ký tự" }).nonempty("Bắt buộc nhập"), //= Số quyết định công nhận
		requestTypeId: z.string().nonempty("Bắt buộc chọn"),
		requestType: z.string().optional(),
		notes: z.string().max(500, { message: "Tối đa 500 ký tự" }).optional(),
		// status: z.string().optional(),
	})
	.superRefine(async (data, ctx) => {
		const { degreeNumber, currentDegreeNumber, cccd, currentCCCD, majorCode, currentMajorCode, studentId, currentStudentId, dateOfBirth, signDate } = data;

		if (!dateOfBirth) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Bắt buộc nhập",
				path: ["dateOfBirth"],
			});
		}

		if (!signDate) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Bắt buộc nhập",
				path: ["signDate"],
			});
		}

		await preCheckExistWrapper<string>(currentDegreeNumber, degreeNumber, async (value) => {
			const exists = await masterDegreesApi.checkExist({ searchValue: value, field: "degreeNumber" });
			if (exists) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Số hiệu bằng đã tồn tại",
					path: ["degreeNumber"],
				});
				return;
			}
		});

		await preCheckExistWrapper<string>(currentStudentId, studentId, async (value) => {
			const exists = await masterDegreesApi.checkExist({ searchValue: value, field: "studentId" });
			if (exists) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Mã sinh viên đã tồn tại",
					path: ["studentId"],
				});
				return;
			}
		});

		if (cccd && majorCode && (cccd !== currentCCCD || majorCode !== currentMajorCode)) {
			const exists = await masterDegreesApi.checkCitizenInfoExist({ citizenId: cccd, majorCode });
			if (exists) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "CCCD và Ngành đã tồn tại",
					path: ["cccd"],
				});

				return;
			}
		}
	});


const regexDate = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const regexNoSpecial = /^[A-Za-z0-9]+$/;
export function validateMasterDegreeImportData(importedScholarship: any[]) {

	const studentIdCounts = {} as any;
	const certificateCounts = {} as any;
	const degreeNumberCounts = {} as any;

	importedScholarship.forEach((item: any) => {
		studentIdCounts[item.studentId] = (studentIdCounts[item.studentId] || 0) + 1;
		certificateCounts[item.recognitionDecisionNumber] = (certificateCounts[item.recognitionDecisionNumber] || 0) + 1;
		degreeNumberCounts[item.degreeNumber] = (degreeNumberCounts[item.degreeNumber] || 0) + 1;
	});


	importedScholarship.forEach((item) => {
		const errors: string[] = [];

		// ===== Mã ngành =====
		if (!item.majorCode) {
			errors.push("Mã ngành không được để trống");
		} else if (!majorCodes.includes(item.majorCode)) {
			errors.push("Mã ngành không hợp lệ");
		}

		// ===== Tên ngành =====
		if (!item.major) {
			errors.push("Tên ngành không được để trống");
		} else {
			// check if major exists in majorCodesWithName
			const majorExists = majorCodesWithName.some((mc) => mc.name === item.major);
			if (!majorExists) {
				errors.push("Tên ngành không hợp lệ");
			}
		}

		// ===== CCCD =====
		if (item.cccd) {
			if (!/^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9}|[a-zA-Z0-9]{12})$/.test(item.cccd)) {
				errors.push("CCCD sai định dạng");
			}
		} else {
			errors.push("CCCD không được để trống");
		}

		// ===== Mã SV =====
		if (item.studentId) {
			if (!item.studentId) {
				errors.push("Mã sinh viên không được để trống");
			} else {
				if (item.studentId.length > 250) errors.push("Mã sinh viên tối đa 250 ký tự");
				if (!regexNoSpecial.test(item.studentId)) {
					errors.push("Mã sinh viên không chứa khoảng trắng hoặc ký tự đặc biệt");
				}
				if (studentIdCounts[item.studentId] > 1) {
					errors.push("Mã sinh viên phải duy nhất trong file");
				}
			}
		} else {
			errors.push("Mã sinh viên không được để trống");
		}

		// ===== Họ tên =====
		if (!item.studentName) {
			errors.push("Họ tên không được để trống");
		} else if (item.studentName.length > 250) {
			errors.push("Họ tên không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Ngày sinh =====
		if (item.dateOfBirth) {
			if (!regexDate.test(item.dateOfBirth)) {
				errors.push("Ngày sinh phải dạng dd/mm/yyyy");
			} else {
				// So sánh theo ngày (bỏ qua giờ/phút) để tránh lệch múi giờ
				const [dd, mm, yyyy] = item.dateOfBirth.split("/").map(Number);
				const dob = new Date(yyyy, (mm || 1) - 1, dd || 1);
				dob.setHours(0, 0, 0, 0);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (dob >= today) {
					errors.push("Ngày sinh không hợp lệ");
				}
			}
		} else {
			errors.push("Ngày sinh không được để trống");
		}
		// ===== Ngày bảo vệ =====
		if (item.defenseDate) {
			if (!regexDate.test(item.defenseDate)) {
				errors.push("Ngày bảo vệ phải dạng dd/mm/yyyy");
			} else {
				// So sánh theo ngày (bỏ qua giờ/phút) để tránh lệch múi giờ
				const [dd, mm, yyyy] = item.defenseDate.split("/").map(Number);
				const defense = new Date(yyyy, (mm || 1) - 1, dd || 1);
				defense.setHours(0, 0, 0, 0);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (defense > today) {
					errors.push("Ngày bảo vệ không được vượt quá ngày hiện tại");
				}
			}
		} else {
			errors.push("Ngày bảo vệ không được để trống");
		}

		// ===== Nơi sinh =====
		if (item.placeOfBirth && item.placeOfBirth.length > 250) {
			errors.push("Nơi sinh không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Giới tính =====
		if (!item.gender) {
			errors.push("Giới tính không được để trống");
		} else {
			const validGenders = ["Nam", "Nữ"];
			if (!validGenders.includes(item.gender)) {
				errors.push("Giới tính không hợp lệ");
			}
		}

		// ===== Dân tộc =====
		if (item.ethnicCode && !ethnicityCodes.includes(item.ethnicCode)) {
			errors.push("Mã dân tộc không hợp lệ");
		}

		// ===== Quốc tịch =====
		if (item.nationalityCode && !nationalityCodes.includes(item.nationalityCode)) {
			errors.push("Mã quốc tịch không hợp lệ");
		}

		// ===== Năm tốt nghiệp =====
		if (!item.graduationYear) {
			errors.push("Năm tốt nghiệp không được để trống");
		} else {
			const year = parseInt(item.graduationYear);
			const now = new Date().getFullYear();
			if (isNaN(year) || year < 1995 || year > now) {
				errors.push("Năm tốt nghiệp không hợp lệ");
			}
		}

		// ===== Hình thức đào tạo =====
		if (!item.trainingMode) {
			errors.push("Hình thức đào tạo không được để trống");
		}

		// ===== Người ký =====
		if (!item.signedBy) {
			errors.push("Người ký không được để trống");
		}

		// ===== Chức danh người ký =====
		if (!item.signerTitle) {
			errors.push("Chức danh người ký không được để trống");
		}

		// ===== Ngày ký =====
		if (!item.signerDate) {
			errors.push("Ngày ký không được để trống");
		} else {
			if (!regexDate.test(item.signerDate)) {
				errors.push("Ngày ký phải dạng dd/mm/yyyy");
			} else {
				// So sánh theo ngày (bỏ qua giờ/phút) để tránh lệch múi giờ
				const [dd, mm, yyyy] = item.signerDate.split("/").map(Number);
				const dob = new Date(yyyy, (mm || 1) - 1, dd || 1);
				dob.setHours(0, 0, 0, 0);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (dob >= today) {
					errors.push("Ngày ký không hợp lệ");
				}
			}
		}

		// ===== Khoá học =====
		if (!item.course) {
			errors.push("Khóa học không được để trống");
		} else if (item.course.length > 250) {
			errors.push("Khóa học không hợp lệ (Tối đa 250 ký tự)");
		}

		// ===== Số hiệu bằng =====
		if (!item.degreeNumber) {
			errors.push("Số hiệu bằng không được để trống");
		} else if (item.degreeNumber.length > 250) {
			errors.push("Số hiệu bằng không hợp lệ (Tối đa 250 ký tự)");
		}

		if (degreeNumberCounts[item.degreeNumber] > 1) {
			errors.push("Số hiệu bằng phải duy nhất trong file");
		}

		// ===== Số vào sổ =====
		if (!item.registrationNumber) {
			errors.push("Số vào sổ không được để trống");
		} else if (item.registrationNumber.length > 50) {
			errors.push("Số vào sổ không hợp lệ (Tối đa 50 ký tự)");
		}

		// ===== Số quyết định =====
		if (!item.recognitionDecisionNumber) {
			errors.push("Số quyết định không được để trống");
		} else if (item.recognitionDecisionNumber.length > 250) {
			errors.push("Số quyết định không hợp lệ (Tối đa 250 ký tự)");
		}

		if (certificateCounts[item.recognitionDecisionNumber] > 1) {
			errors.push("Số quyết định phải duy nhất trong file");
		}

		// ===== Loại yêu cầu và Mã loại yêu cầu =====

		if (!item.requestTypeId) {
			errors.push("Mã loại yêu cầu không được để trống");
		} else {
			const validRequestTypeIds = requestTypeList.map(r => r.code);
			if (!validRequestTypeIds.includes(item.requestTypeId)) {
				errors.push("Mã loại yêu cầu không hợp lệ");
			}
		}

		if (!item.requestType) {
			errors.push("Loại yêu cầu không được để trống");
		} else {
			const validRequestTypes = requestTypeList.map(r => r.name);
			if (!validRequestTypes.includes(item.requestType)) {
				errors.push("Loại yêu cầu không hợp lệ");
			}
		}

		// ===== Ghi chú =====
		if (item.notes && item.notes.length > 500) {
			errors.push("Ghi chú không hợp lệ (Tối đa 500 ký tự)");
		}

		item.errors = errors;
	});

	return importedScholarship;
}


export async function validateMasterDegreeImportDataInDB(importedScholarship: any[]) {

	for (const item of importedScholarship) {
		if (!item.errors) {
			item.errors = [];
		}
		const studentIdExisting = await masterDegreesApi.checkExist({ searchValue: item.studentId, field: "studentId" });
		if (studentIdExisting) {
			item.errors.push("Mã sinh viên đã tồn tại");
		}
		const degreeNumberExisting = await masterDegreesApi.checkExist({ searchValue: item.degreeNumber, field: "degreeNumber" });
		if (degreeNumberExisting) {
			item.errors.push("Số hiệu bằng đã tồn tại");
		}
		const citizenInfoExisting = await masterDegreesApi.checkCitizenInfoExist({ citizenId: item.cccd, majorCode: item.majorCode });
		if (citizenInfoExisting) {
			item.errors.push("CCCD và Ngành đã tồn tại");
		}

	}
	return importedScholarship;
}